import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Visibility, FilterAltOff, Close } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}/audit-logs`;

export default function OrganizationAuditLogsTab() {
  const { permissions, activeOrg } = useAuth();
  const canView = can(permissions, "audit.view") || permissions.has("*");

  const [rows, setRows] = useState([]);
  const emailOptions = useMemo(() => {
    const emails = rows.map((row) => row.email).filter(Boolean); // remove null/undefined

    return [...new Set(emails)]; // remove duplicates
  }, [rows]);
  const actionOptions = useMemo(() => {
    const actions = rows.map((row) => row.action).filter(Boolean);

    return [...new Set(actions)];
  }, [rows]);
  const entityOptions = useMemo(() => {
    const entities = rows.map((row) => row.entity_type).filter(Boolean);

    return [...new Set(entities)];
  }, [rows]);

  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    action: "",
    entity_type: "",
    actor_email: "",
    from: "",
    to: "",
  });

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const actionMatch = filters.action ? row.action === filters.action : true;

      const emailMatch = filters.actor_email
        ? row.email === filters.actor_email
        : true;

      const entityMatch = filters.entity_type
        ? row.entity_type === filters.entity_type
        : true;

      return actionMatch && emailMatch && entityMatch;
    });
  }, [rows, filters]);

  const [error, setError] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [rowCount, setRowCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);

  // Reset to first page when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filters]);

  useEffect(() => {
    if (!canView) return;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("authToken");

        const res = await axios.get(API_BASE, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
          },
          params: {
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            ...filters,
          },
        });

        const data = res.data;

        console.log(data, 98);

        setRows(data.items || data.results || data || []);
        setRowCount(data.total || data.count || 0);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load audit logs");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [paginationModel, filters, activeOrg, canView]);

  const resetFilters = () => {
    setFilters({
      action: "",
      entity_type: "",
      actor_email: "",
      from: "",
      to: "",
    });
  };

  const columns = useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        minWidth: 180,
        renderCell: (params) =>
          params.value
            ? dayjs(params.value).format("YYYY-MM-DD HH:mm:ss")
            : "—",
      },
      {
        field: "email",
        headerName: "Actor",
        flex: 1,
        type: "singleSelect",
        valueOptions: emailOptions,
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 160,
        renderCell: (params) => (
          <Chip label={params.value} size="small" variant="outlined" />
        ),
      },
      {
        field: "entity_type",
        headerName: "Entity",
        minWidth: 160,
      },
      {
        field: "entity_id",
        headerName: "Entity ID",
        flex: 1,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => setSelectedLog(params.row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  if (!canView) {
    return (
      <Typography sx={{ mt: 3 }} color="text.secondary">
        You don’t have permission to view audit logs.
      </Typography>
    );
  }

  return (
    <>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* FILTER BAR */}
      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            select
            label="Action"
            size="small"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            sx={{ width: "100%" }}
          >
            <MenuItem value="">All</MenuItem>

            {actionOptions.map((action) => (
              <MenuItem key={action} value={action}>
                {action}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Entity"
            size="small"
            value={filters.entity_type}
            onChange={(e) =>
              setFilters({
                ...filters,
                entity_type: e.target.value,
              })
            }
            sx={{ width: "100%" }}
          >
            <MenuItem value="">All</MenuItem>

            {entityOptions.map((entity) => (
              <MenuItem key={entity} value={entity}>
                {entity}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Actor"
            size="small"
            value={filters.actor_email}
            onChange={(e) =>
              setFilters({
                ...filters,
                actor_email: e.target.value,
              })
            }
            sx={{ width: "100%" }}
          >
            <MenuItem value="">All</MenuItem>

            {emailOptions.map((email) => (
              <MenuItem key={email} value={email}>
                {email}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            size="small"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            sx={{ width: "100%" }}
          />

          <TextField
            type="date"
            size="small"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            sx={{ width: "100%" }}
          />

          <Button onClick={resetFilters} startIcon={<FilterAltOff />}>
            Clear
          </Button>
        </Stack>
      </Box>

      {/* TABLE */}
      <Box sx={{ height: 500, width: "100%" }}>
        {/* <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        // paginationMode="server"
        // rowCount={rowCount}
        pagination
        pageSizeOptions={[10, 25, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        disableRowSelectionOnClick
        rowHeight={48}
      /> */}
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      {/* DETAILS DRAWER */}
      <Drawer
        anchor="right"
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
      >
        <Box sx={{ width: { xs: "100%", md: 420 }, p: 0 }}>

          {/* HEADER */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1.5,
              background: "linear-gradient(135deg, #0f172a 0%, #134e4a 50%, #0ea5a4 100%)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
                fontWeight: 600,
                letterSpacing: 0.3,
              }}
            >
              Audit Details
            </Typography>

            <IconButton
              onClick={() => setSelectedLog(null)}
              sx={{
                color: "#fff",
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* CONTENT */}
          <Box sx={{ p: 2 }}>

            {selectedLog ? (
              <>
                <Detail
                  label="Time"
                  value={
                    selectedLog.created_at
                      ? dayjs(selectedLog.created_at).format(
                        "YYYY-MM-DD HH:mm:ss"
                      )
                      : "-"
                  }
                />
                <Detail label="Action" value={selectedLog.action} />
                <Detail label="Entity Type" value={selectedLog.entity_type} />
                <Detail label="Entity ID" value={selectedLog.entity_id} />

                <Divider sx={{ my: 2 }} />

                <Typography fontWeight={600} gutterBottom>
                  Metadata
                </Typography>

                <Box
                  sx={{
                    bgcolor: "#f5f5f5",
                    p: 1,
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    maxHeight: 300,
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(selectedLog.metadata || {}, null, 2)}
                </Box>
              </>
            ) : (
              <CircularProgress />
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

function Detail({ label, value }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography fontSize={12} color="text.secondary">
        {label}
      </Typography>
      <Typography fontSize={14}>{value || "-"}</Typography>
    </Box>
  );
}
